// src/pages/gallery-profile-options/artists-management/virtual-list/artist-mgmt-card/artist-details/artist-details.jsx
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import config         from '@/config';                              // <<< nuevo >>>
import useStateHandler from '@/hooks/useStateHandler';
import fetchWrapper   from '@/services/fetchWrapper.service';      // <<< nuevo >>>
import GalleryService from '@/services/gallery.service';           // <<< nuevo >>>
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import CreateArtist   from '@/shared-components/forms/create-artist';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import ToggleSwitch   from '@/shared-components/toggles/ToggleSwitch';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import './artist-details.css';

export default function ArtistDetails() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const outletContext = useOutletContext();
  const parentArtist = outletContext?.artist ?? null;
  const setParentArtist = outletContext?.setArtist;
  const parentPermsDoc = outletContext?.permsDoc ?? null;
  const setParentPermsDoc = outletContext?.setPermsDoc;
  const initialArtist  = parentArtist ?? location.state?.artist;
  const initialPermDoc = (parentPermsDoc ?? location.state?.permissions) || null;      // <<< nuevo >>>

  const [artist,    setArtist]    = useState(initialArtist);
  const [permsDoc,  setPermsDoc]  = useState(initialPermDoc);      // <<< nuevo >>>
  const [isEditing, setIsEditing] = useState(false);
  const [showClaimedPopup, setShowClaimedPopup] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState('');
  const { state } = useStateHandler();
  const session = state?.user_session;
  const galleryId = session?.gallery_id || session?._id;
  const canViewArtistDetails = canAccessGalleryPermission(session, 'artists.view_details', galleryId);
  const canUpdateArtistPermissions = canAccessGalleryPermission(session, 'artists.update_permissions', galleryId);
  const HIDDEN_EMAIL_SNIPPET = 'artist.suarte.art';
  const emailVisible =
    artist?.artist_email &&
    !artist.artist_email.toLowerCase().includes(HIDDEN_EMAIL_SNIPPET);
  const isArtistClaimed = Boolean(artist?.flags?.claimed);

  useEffect(() => {
    if (parentArtist && parentArtist !== artist) {
      setArtist(parentArtist);
    }
  }, [artist, parentArtist]);

  useEffect(() => {
    if (parentPermsDoc && parentPermsDoc !== permsDoc) {
      setPermsDoc(parentPermsDoc);
    }
  }, [parentPermsDoc, permsDoc]);

  /* ─── Si entramos directo (sin perms) cargamos permisos ─── */   // <<< nuevo >>>
  useEffect(() => {
    (async () => {
      if (!permsDoc && artist && canViewArtistDetails) {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/permissions?artist=${artist._id}&gallery_id=${encodeURIComponent(galleryId || '')}`,
          { injectToken: true }
        );
        if (response.ok) {
          const nextPerms = data[0] || null;
          setPermsDoc(nextPerms);
          setParentPermsDoc?.(nextPerms);
        }
      }
    })();
  }, [artist, canViewArtistDetails, galleryId, permsDoc, setParentPermsDoc]);

  /* ─── Cambiar un flag y hacer PUT ─── */                        // <<< nuevo >>>
  const togglePermission = async (key) => {
    if (!isArtistClaimed) {
      setPermissionMessage('Artist permissions can only be applied to artists who have already claimed their profile.');
      setShowClaimedPopup(true);
      return;
    }
    if (!canUpdateArtistPermissions) {
      setPermissionMessage('You do not have permission to update artist permissions.');
      setShowClaimedPopup(true);
      return;
    }
    const newPermissions = {
      ...p,
      [key]: !p[key]
    };
    const request = permsDoc?._id
      ? GalleryService.updatePermissions(permsDoc._id, newPermissions, galleryId)
      : GalleryService.createPermissions(artist._id, newPermissions, galleryId);
    const { response, data } = await request;
    if (response.ok) {
      setPermsDoc(data);
      setParentPermsDoc?.(data);
    } else {
      setPermissionMessage(data?.error_message || 'Unable to update artist permissions.');
      setShowClaimedPopup(true);
    }
  };

  if (!artist) {
    return (
      <div className="artist-details__notfound">
        <p>Artist data not available.</p>
        <ContinueButton onClick={() => navigate('/my-artists')}>
          Back to artists
        </ContinueButton>
      </div>
    );
  }

  const p = permsDoc?.permissions ?? {};   // abreviatura           // <<< nuevo >>>

  return (
    <div className="artist-details">
      {/* -------- ARTIST INFORMATION -------- */}
      <section className="artist-details__info">
        {/* ------------- (sin cambios) ------------- */}
        <h2>Artist Information</h2>
        <ul>
          <li><strong>Name:</strong> {artist.artist_name}</li>
          <li>
            <strong>Born in:</strong>{' '}
            {artist.artist_nationality || '–'},{' '}
            {artist.artist_birth || '–'}
          </li>
          {artist.artist_death && (
            <li>
              <strong>Artist Death:</strong> {artist.artist_death}
            </li>
          )}
          <li>
            <strong>Email:</strong> {emailVisible ? artist.artist_email : '–'}
          </li>
          <li className="artist-details__type-of-artwork">
            <strong>Type of Artwork:</strong>
            <div className="type-of-artwork__counts">
              {artist.countWorkOfArtist !== 0 && (
                <span className="type-of-artwork__item">
                  Work of Artist:{' '}
                  <span className="type-of-artwork__value">
                    {artist.countWorkOfArtist ?? 0}
                  </span>
                </span>
              )}
              {artist.countGalleryOwned !== 0 && (
                <span className="type-of-artwork__item">
                  Gallery Owned:{' '}
                  <span className="type-of-artwork__value">
                    {artist.countGalleryOwned ?? 0}
                  </span>
                </span>
              )}
            </div>
          </li>
          <li>
            <strong>Profile claimed:</strong>{' '}
            {artist.flags?.claimed ? 'Yes' : 'No'}
          </li>
          <li>
            <strong>Created on:</strong>{' '}
            {artist.createdAt
              ? new Date(artist.createdAt).toLocaleDateString()
              : '–'}
          </li>
        </ul>
        <ContinueButton
          className="artist-details__edit-btn"
          onClick={() => setIsEditing(true)}
        >
          Edit information
        </ContinueButton>
      </section>

      {/* -------- ARTIST PERMISSIONS -------- */}
      <section
        className="artist-details__perms"
        onClick={(event) => {
          if (!isArtistClaimed || !canUpdateArtistPermissions) {
            event.preventDefault();
            event.stopPropagation();
            setPermissionMessage(
              !isArtistClaimed
                ? 'Artist permissions can only be applied to artists who have already claimed their profile.'
                : 'You do not have permission to update artist permissions.'
            );
            setShowClaimedPopup(true);
          }
        }}
      >
        <h2>Artist Permissions</h2>
        <p>
          When one of your artists claims their profile, you can grant them
          access to edit or even upload their artworks.
        </p>

        {/* --- INFORMATION --- */}
        <h3>Edit Artwork’s Information</h3>
        <div className="artist-details__permission-group">
          {[
            ['titleDescription', 'Title and description'],
            ['medium',           'Medium'],
            ['yearOfCreation',   'Year of creation'],
            ['dimensions',       'Dimensions'],
            ['categories',       'Colors, themes and emotions'],
            ['requestLimitedEditions', 'Request Limited Editions'],
            ['frameCertificate',  'Frame and Certificate'],
          ].map(([key, label]) => (
            <div className="permission-row" key={key}>
              <span className="permission-label">{label}</span>
              <ToggleSwitch
                checked={!!p[key]}
                onChange={() => togglePermission(key)}
              />
            </div>
          ))}
        </div>

        {/* --- MEDIA --- */}
        <h3>Edit Artwork’s Media</h3>
        <div className="artist-details__permission-group">
          {[
            ['mainPicture',       'Main picture'],
            ['secondaryPictures', 'Secondary pictures'],
            ['audio',             'Audio'],
          ].map(([key, label]) => (
            <div className="permission-row" key={key}>
              <span className="permission-label">{label}</span>
              <ToggleSwitch
                checked={!!p[key]}
                onChange={() => togglePermission(key)}
              />
            </div>
          ))}
        </div>

        {/* --- PRICE --- */}
        <h3>Edit Price</h3>
        <div className="artist-details__permission-group">
          <div className="permission-row">
            <span className="permission-label">Artwork Price</span>
            <ToggleSwitch
              checked={!!p.price}
              onChange={() => togglePermission('price')}
            />
          </div>
          <div className="permission-row">
            <span className="permission-label">Shipping Tariffs</span>
            <ToggleSwitch
              checked={!!p.shippingTariffs}
              onChange={() => togglePermission('shippingTariffs')}
            />
          </div>
        </div>

        {/* --- ADD ARTWORKS --- */}
        <h3>Add Artworks</h3>
        <div className="artist-details__permission-group">
          <div className="permission-row">
            <span className="permission-label">Add artworks</span>
            <ToggleSwitch
              checked={!!p.addArtworks}
              onChange={() => togglePermission('addArtworks')}
            />
          </div>
        </div>
      </section>

      {/* -------- POPUP DE EDICIÓN -------- */}
      <CreateArtist
        open={isEditing}
        close={() => setIsEditing(false)}
        artistData={artist}
        createMode={artist.type}
        editMode={true}
        onCreate={(updatedArtist) => {
          setArtist(updatedArtist);
          setParentArtist?.(updatedArtist);
          setIsEditing(false);
        }}
      />
      <ActionRejectedPopup
        open={showClaimedPopup}
        close={() => setShowClaimedPopup(false)}
        description={permissionMessage}
      />
    </div>
  );
}
